import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const sponsorHighlight = {
  name: "SparkFX LLC",
  tagline: "Driving agility, innovation, and happiness",
  description: "SparkFX LLC supports our organization to make Python education accessible to all students.",
  logo: "/sponsor-logo.png",
  website: "https://sparkfx.net"
};

export const SponsorSection = () => {
  const [logoSrc, setLogoSrc] = useState(sponsorHighlight.logo);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden isolate">
      <div
        aria-hidden
        className="absolute inset-0 opacity-80"
        style={{
          backgroundImage: "var(--gradient-site)",
          backgroundAttachment: "fixed",
          backgroundSize: "100% 200%"
        }}
      />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,hsl(48_100%_61%/.14),transparent_45%),radial-gradient(circle_at_80%_30%,hsl(211_52%_44%/.14),transparent_50%)]" />
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-background/90 via-background/40 to-transparent" aria-hidden />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-5xl mx-auto space-y-12 text-center"
        >
          <motion.div variants={item} className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary/80">
              Sponsor Spotlight
            </p>
            <h2 className="text-4xl sm:text-5xl font-light text-foreground">
              Grateful for partners who believe in <span className="text-primary">our mission</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              This program thrives with community champions. Meet the organization making our current cohort possible.
            </p>
          </motion.div>

          <motion.div
            variants={item}
            className="grid lg:grid-cols-[minmax(220px,260px)_1fr] gap-10 lg:gap-16 items-center bg-white/90 border border-border rounded-2xl shadow-card backdrop-blur-sm p-8"
          >
            <div className="flex flex-col items-center gap-6">
              <div className="w-full max-w-[220px] rounded-xl border border-border/80 bg-gradient-to-b from-white to-muted/20 px-6 py-8 shadow-inner">
                <img
                  src={logoSrc}
                  alt={`${sponsorHighlight.name} logo`}
                  width={270}
                  height={50}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                  onError={() => setLogoSrc("/placeholder.svg")}
                />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Top Sponsor</p>
                <p className="mt-2 text-base font-medium text-foreground">{sponsorHighlight.name}</p>
              </div>
            </div>

            <div className="space-y-6 text-left">
              <div>
                <p className="text-sm font-semibold text-primary tracking-wide">{sponsorHighlight.tagline}</p>
                <p className="mt-3 text-lg text-muted-foreground leading-relaxed">
                  {sponsorHighlight.description}
                </p>
              </div>
              <a
                href={sponsorHighlight.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
              >
                Visit {sponsorHighlight.name}
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
